import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Building2, CreditCard, TrendingUp, DollarSign, BarChart3, Zap,
  ArrowUpRight, ArrowDownRight, AlertCircle, Wallet, Clock, Gift, Handshake
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart as ReBarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { API } from './adminConstants';
import { brl } from '@/lib/utils';

const NAVY = '#0A2647';
const GOLD = '#C5A059';
const GREEN = '#10B981';

const RANGES = [
  { id: 'today', label: 'Hoje' },
  { id: '7d', label: '7 dias' },
  { id: '30d', label: '30 dias' },
  { id: 'month', label: 'Mês atual' },
  { id: 'custom', label: 'Período' },
];

const CustomTooltip = ({ active, payload, label, money }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#111827] border border-white/10 rounded-xl px-4 py-3 shadow-2xl">
      <p className="text-[#C5A059] text-xs font-bold mb-1.5">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-white/80 text-xs">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: p.color }} />
          {p.name}: <span className="font-bold text-white">{money ? brl(p.value) : p.value}</span>
        </p>
      ))}
    </div>
  );
};

const KpiCard = ({ icon: Icon, tint, value, label, sub, testid }) => (
  <div className="bg-[#111827] rounded-2xl p-5 border border-white/5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${tint}1A` }}>
        <Icon className="w-5 h-5" style={{ color: tint }} />
      </div>
      {sub && <span className="text-[10px] font-medium text-slate-500 text-right max-w-[110px]">{sub}</span>}
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-slate-500 mt-1">{label}</p>
  </div>
);

const AdminDashboardTab = () => {
  const { getToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const range = searchParams.get('range') || '30d';
  const dateFrom = searchParams.get('from') || '';
  const dateTo = searchParams.get('to') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (range === 'custom' && !(dateFrom && dateTo)) { setLoading(false); return; }
    setLoading(true);
    try {
      const qs = range === 'custom' ? `range=custom&from=${dateFrom}&to=${dateTo}` : `range=${range}`;
      const res = await axios.get(`${API}/admin/dashboard-summary?${qs}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setData(res.data);
    } catch { toast.error('Erro ao carregar dashboard'); }
    finally { setLoading(false); }
  }, [getToken, range, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setRange = (id) => {
    const next = { range: id };
    if (id === 'custom' && dateFrom && dateTo) { next.from = dateFrom; next.to = dateTo; }
    setSearchParams(next);
  };

  const PeriodBar = () => (
    <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-period-bar">
      {RANGES.map(r => (
        <button key={r.id} onClick={() => setRange(r.id)} data-testid={`period-${r.id}`}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
            range === r.id ? 'bg-[#C5A059] text-[#0A2647]' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}>
          {r.label}
        </button>
      ))}
      {range === 'custom' && (
        <div className="flex items-center gap-2">
          <input type="date" value={dateFrom} data-testid="period-from"
            onChange={e => setSearchParams({ range: 'custom', from: e.target.value, to: dateTo })}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-white" />
          <span className="text-slate-500 text-xs">até</span>
          <input type="date" value={dateTo} data-testid="period-to"
            onChange={e => setSearchParams({ range: 'custom', from: dateFrom, to: e.target.value })}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-white" />
        </div>
      )}
    </div>
  );

  if (loading) return (
    <div className="space-y-6"><PeriodBar />
      <div className="flex justify-center py-20"><div className="animate-spin w-10 h-10 border-4 border-[#C5A059] border-t-transparent rounded-full" /></div>
    </div>
  );
  if (!data) return (
    <div className="space-y-6"><PeriodBar />
      <div className="py-20 text-center text-slate-400 text-sm">Selecione um período para carregar as métricas.</div>
    </div>
  );

  const { period, carteira, faturamento, afiliados, parceiros, avaliacoes, series, consistencia } = data;
  const cobranca = data.cobranca || { em_dia: 0, a_vencer: 0, inadimplentes: 0, sem_cobranca: 0, lista_inadimplentes: [] };
  const pagantes = carteira.pagantes;
  const naoPagantes = carteira.nao_pagantes;
  const pagos = carteira.por_plano.filter(p => p.pagante);
  const gratis = carteira.por_plano.filter(p => !p.pagante);
  const totalQtd = carteira.por_plano.reduce((s, p) => s + p.qtd, 0);

  const PlanRows = ({ rows }) => rows.map(p => (
    <tr key={p.plano_id} className="border-b border-white/5" data-testid={`plan-row-${p.plano_id}`}>
      <td className="py-2 pr-3 text-slate-200">{p.plano}</td>
      <td className="py-2 pr-3 text-right text-white font-semibold">{p.qtd}</td>
      <td className="py-2 pr-3 text-right text-slate-400">{p.qtd_ativas}</td>
      <td className="py-2 pr-3 text-right text-slate-400">{p.pct.toFixed(1)}%</td>
      <td className="py-2 pr-3 text-center">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.pagante ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-500/15 text-slate-400'}`}>
          {p.pagante ? 'Sim' : 'Não'}
        </span>
      </td>
      <td className="py-2 pr-3 text-right text-slate-300">{brl(p.valor_unitario)}</td>
      <td className="py-2 text-right text-white font-semibold">{brl(p.mrr)}</td>
    </tr>
  ));

  return (
    <div className="space-y-6" data-testid="dashboard-section">
      <PeriodBar />

      {!consistencia.ok && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="dashboard-alerts">
          <p className="text-xs font-bold text-amber-300 flex items-center gap-2 mb-1.5">
            <AlertCircle className="w-4 h-4" />Inconsistências detectadas
          </p>
          <ul className="list-disc list-inside space-y-1">
            {consistencia.alertas.map((a, i) => <li key={i} className="text-[11px] text-amber-200/80">{a}</li>)}
          </ul>
        </div>
      )}

      {/* Linha 1 — Carteira (foto de agora) */}
      <div>
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Carteira · foto de agora</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testid="kpi-ativas" icon={Building2} tint={GOLD} value={carteira.ativas}
            label="Empresas Ativas" sub={`${carteira.total_cadastradas} cadastradas · ${carteira.canceladas} fora`} />
          <KpiCard testid="kpi-pagantes" icon={CreditCard} tint={GREEN}
            value={`${pagantes.qtd} · ${pagantes.pct.toFixed(1)}%`} label="Pagantes" sub="cobrança vigente" />
          <KpiCard testid="kpi-nao-pagantes" icon={Gift} tint="#94A3B8"
            value={`${naoPagantes.qtd} · ${naoPagantes.pct.toFixed(1)}%`} label="Não Pagantes"
            sub={`inclui ${carteira.cortesia_trial.qtd} em cortesia`} />
          <KpiCard testid="kpi-mrr" icon={DollarSign} tint={GREEN} value={brl(faturamento.previsto_mrr)}
            label="MRR Previsto" sub={`${pagantes.qtd} pagantes · cortesia ${brl(carteira.cortesia_trial.mrr_potencial)}`} />
        </div>
      </div>

      {/* Linha 2 — Financeiro do período */}
      <div>
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
          Financeiro · {period.label}
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testid="kpi-recebido" icon={Wallet} tint={GREEN} value={brl(faturamento.recebido_periodo)}
            label="Faturamento Recebido" sub={period.label} />
          <KpiCard testid="kpi-a-receber" icon={Clock} tint={GOLD} value={brl(faturamento.a_receber_periodo)}
            label="A Receber" sub={period.label} />
          <KpiCard testid="kpi-vencido" icon={AlertCircle} tint="#EF4444" value={brl(faturamento.vencido_periodo)}
            label="Vencido" sub={period.label} />
          <KpiCard testid="kpi-ticket" icon={TrendingUp} tint={GOLD} value={brl(faturamento.ticket_medio_pagante)}
            label="Ticket Médio por Pagante" sub="foto de agora" />
        </div>
      </div>

      {/* Situação de cobrança — quem está em dia e quem está inadimplente */}
      <div className="bg-[#111827] rounded-2xl border border-white/5 p-6" data-testid="billing-situation">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <CreditCard className="w-4 h-4" style={{ color: GREEN }} />Situação de cobrança
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Direto do Asaas · foto de agora
              {cobranca.atualizado_em ? ` · atualizado ${new Date(cobranca.atualizado_em).toLocaleString('pt-BR')}` : ''}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Em atraso</p>
            <p className="text-lg font-bold text-red-400" data-testid="billing-overdue-value">{brl(cobranca.valor_em_atraso || 0)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div className="bg-black/20 rounded-xl p-4" data-testid="billing-ok">
            <p className="text-2xl font-bold" style={{ color: GREEN }}>{cobranca.em_dia}</p>
            <p className="text-xs text-slate-400 mt-1">Em dia</p>
            <p className="text-[10px] text-slate-500">assinatura ativa, sem boleto aberto</p>
          </div>
          <div className="bg-black/20 rounded-xl p-4" data-testid="billing-due">
            <p className="text-2xl font-bold" style={{ color: GOLD }}>{cobranca.a_vencer}</p>
            <p className="text-xs text-slate-400 mt-1">A vencer</p>
            <p className="text-[10px] text-slate-500">{brl(cobranca.valor_a_vencer || 0)} em boletos no prazo</p>
          </div>
          <div className="bg-black/20 rounded-xl p-4" data-testid="billing-overdue">
            <p className="text-2xl font-bold text-red-400">{cobranca.inadimplentes}</p>
            <p className="text-xs text-slate-400 mt-1">Inadimplentes</p>
            <p className="text-[10px] text-slate-500">com boleto vencido</p>
          </div>
          <div className="bg-black/20 rounded-xl p-4" data-testid="billing-none">
            <p className="text-2xl font-bold text-slate-300">{cobranca.sem_cobranca}</p>
            <p className="text-xs text-slate-400 mt-1">Sem cobrança</p>
            <p className="text-[10px] text-slate-500">plano pago no app, sem assinatura no Asaas</p>
          </div>
        </div>

        {cobranca.lista_inadimplentes.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="billing-overdue-table">
              <thead>
                <tr className="text-slate-500 border-b border-white/10">
                  <th className="text-left py-2 pr-3 font-medium">Empresa</th>
                  <th className="text-right py-2 pr-3 font-medium">Valor</th>
                  <th className="text-right py-2 pr-3 font-medium">Atraso</th>
                  <th className="text-right py-2 pr-3 font-medium">Vencimento</th>
                  <th className="text-right py-2 font-medium">Boleto</th>
                </tr>
              </thead>
              <tbody>
                {cobranca.lista_inadimplentes.map(c => (
                  <tr key={c.company_id} className="border-b border-white/5" data-testid={`overdue-row-${c.company_id}`}>
                    <td className="py-2 pr-3 text-slate-200">{c.empresa}</td>
                    <td className="py-2 pr-3 text-right text-white font-semibold">{brl(c.valor)}</td>
                    <td className="py-2 pr-3 text-right text-red-400 font-semibold">{c.dias_atraso} dia(s)</td>
                    <td className="py-2 pr-3 text-right text-slate-400">
                      {c.vencimento ? new Date(`${c.vencimento}T12:00:00`).toLocaleDateString('pt-BR') : '—'}
                    </td>
                    <td className="py-2 text-right">
                      {c.invoice_url
                        ? <a href={c.invoice_url} target="_blank" rel="noreferrer" className="text-[#C5A059] font-semibold hover:underline">abrir</a>
                        : <span className="text-slate-500">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-slate-500">Nenhuma empresa inadimplente agora. 🎉</p>
        )}
      </div>

      {/* Barra Pagantes x Não Pagantes */}
      <div className="bg-[#111827] rounded-2xl border border-white/5 p-6" data-testid="paying-split">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h3 className="text-sm font-bold text-white">Pagantes x Não Pagantes</h3>
            <p className="text-xs text-slate-500 mt-0.5">Sobre {carteira.ativas} empresas ativas · foto de agora</p>
          </div>
          <div className="flex gap-6 text-right">
            <div>
              <p className="text-2xl font-bold" style={{ color: GREEN }} data-testid="split-paying">{pagantes.qtd}</p>
              <p className="text-[11px] text-slate-400">pagantes · {pagantes.pct.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-300" data-testid="split-nonpaying">{naoPagantes.qtd}</p>
              <p className="text-[11px] text-slate-400">não pagantes · {naoPagantes.pct.toFixed(1)}%</p>
            </div>
          </div>
        </div>
        <div className="h-8 w-full rounded-full overflow-hidden flex bg-[#0A2647]">
          <div className="h-full flex items-center justify-center transition-all"
            style={{ width: `${pagantes.pct}%`, backgroundColor: GREEN }}>
            {pagantes.pct >= 12 && <span className="text-[11px] font-bold text-[#04231a]">{pagantes.pct.toFixed(1)}%</span>}
          </div>
          <div className="h-full flex items-center justify-center transition-all"
            style={{ width: `${naoPagantes.pct}%`, backgroundColor: NAVY, borderLeft: '1px solid rgba(255,255,255,.15)' }}>
            {naoPagantes.pct >= 12 && <span className="text-[11px] font-bold text-white/80">{naoPagantes.pct.toFixed(1)}%</span>}
          </div>
        </div>
      </div>

      {/* Distribuição de Planos */}
      <div className="bg-[#111827] rounded-2xl border border-white/5 p-6" data-testid="plans-table">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-1">
          <CreditCard className="w-4 h-4 text-[#C5A059]" />Distribuição de Planos
        </h3>
        <p className="text-xs text-slate-500 mb-4">Todas as empresas cadastradas · foto de agora</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500 border-b border-white/10">
                <th className="text-left font-medium pb-2 pr-3">Plano</th>
                <th className="text-right font-medium pb-2 pr-3">Qtd</th>
                <th className="text-right font-medium pb-2 pr-3">Ativas</th>
                <th className="text-right font-medium pb-2 pr-3">%</th>
                <th className="text-center font-medium pb-2 pr-3">Pagante?</th>
                <th className="text-right font-medium pb-2 pr-3">Valor</th>
                <th className="text-right font-medium pb-2">MRR</th>
              </tr>
            </thead>
            <tbody>
              <tr><td colSpan={7} className="pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: GREEN }}>Pagantes</td></tr>
              <PlanRows rows={pagos} />
              <tr><td colSpan={7} className="pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Não pagantes (Free, LIVRE, cortesia)</td></tr>
              <PlanRows rows={gratis} />
              <tr className="border-t border-white/20" data-testid="plans-total-row">
                <td className="py-2.5 pr-3 font-bold text-white">Total</td>
                <td className="py-2.5 pr-3 text-right font-bold text-white">{totalQtd}</td>
                <td className="py-2.5 pr-3 text-right font-bold text-slate-300">{carteira.ativas}</td>
                <td className="py-2.5 pr-3" />
                <td className="py-2.5 pr-3" />
                <td className="py-2.5 pr-3" />
                <td className="py-2.5 text-right font-bold" style={{ color: GREEN }}>{brl(faturamento.previsto_mrr)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Vendas por Afiliado / Parceiro */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#111827] rounded-2xl border border-white/5 p-6" data-testid="affiliates-table">
          <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4" style={{ color: GREEN }} />Vendas por Afiliado
          </h3>
          <p className="text-xs text-slate-500 mb-4">{period.label}</p>
          {afiliados.length === 0 ? (
            <p className="text-xs text-slate-500 py-6 text-center">Sem vendas no período.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-white/10">
                  <th className="text-left font-medium pb-2">Afiliado</th>
                  <th className="text-right font-medium pb-2">Ativações</th>
                  <th className="text-right font-medium pb-2">Vendido</th>
                  <th className="text-right font-medium pb-2">Paga</th>
                  <th className="text-right font-medium pb-2">Pendente</th>
                </tr>
              </thead>
              <tbody>
                {afiliados.map(a => (
                  <tr key={a.codigo || a.nome} className="border-b border-white/5">
                    <td className="py-2 text-slate-200">{a.nome} <span className="text-slate-500">{a.codigo}</span></td>
                    <td className="py-2 text-right text-white">{a.ativacoes}</td>
                    <td className="py-2 text-right text-slate-300">{brl(a.valor_vendido_mrr)}</td>
                    <td className="py-2 text-right" style={{ color: GREEN }}>{brl(a.comissao_paga)}</td>
                    <td className="py-2 text-right text-[#C5A059]">{brl(a.comissao_pendente)}</td>
                  </tr>
                ))}
                <tr className="border-t border-white/20">
                  <td className="py-2 font-bold text-white">Total</td>
                  <td className="py-2 text-right font-bold text-white">{afiliados.reduce((s, a) => s + a.ativacoes, 0)}</td>
                  <td className="py-2 text-right font-bold text-slate-200">{brl(afiliados.reduce((s, a) => s + a.valor_vendido_mrr, 0))}</td>
                  <td className="py-2 text-right font-bold" style={{ color: GREEN }}>{brl(afiliados.reduce((s, a) => s + a.comissao_paga, 0))}</td>
                  <td className="py-2 text-right font-bold text-[#C5A059]">{brl(afiliados.reduce((s, a) => s + a.comissao_pendente, 0))}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-[#111827] rounded-2xl border border-white/5 p-6" data-testid="partners-table">
          <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-1">
            <Handshake className="w-4 h-4 text-[#C5A059]" />Vendas por Parceiro
          </h3>
          <p className="text-xs text-slate-500 mb-4">{period.label}</p>
          {parceiros.length === 0 ? (
            <p className="text-xs text-slate-500 py-6 text-center">Sem vendas no período.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-white/10">
                  <th className="text-left font-medium pb-2">Parceiro</th>
                  <th className="text-right font-medium pb-2">Empresas</th>
                  <th className="text-right font-medium pb-2">MRR</th>
                  <th className="text-right font-medium pb-2">Recebido</th>
                  <th className="text-right font-medium pb-2">Comissão</th>
                </tr>
              </thead>
              <tbody>
                {parceiros.map(p => (
                  <tr key={p.nome} className="border-b border-white/5">
                    <td className="py-2 text-slate-200">{p.nome}</td>
                    <td className="py-2 text-right text-white">{p.empresas_carteira}</td>
                    <td className="py-2 text-right text-slate-300">{brl(p.mrr_carteira)}</td>
                    <td className="py-2 text-right" style={{ color: GREEN }}>{brl(p.recebido_periodo)}</td>
                    <td className="py-2 text-right text-[#C5A059]">{brl(p.comissao_periodo)}</td>
                  </tr>
                ))}
                <tr className="border-t border-white/20">
                  <td className="py-2 font-bold text-white">Total</td>
                  <td className="py-2 text-right font-bold text-white">{parceiros.reduce((s, p) => s + p.empresas_carteira, 0)}</td>
                  <td className="py-2 text-right font-bold text-slate-200">{brl(parceiros.reduce((s, p) => s + p.mrr_carteira, 0))}</td>
                  <td className="py-2 text-right font-bold" style={{ color: GREEN }}>{brl(parceiros.reduce((s, p) => s + p.recebido_periodo, 0))}</td>
                  <td className="py-2 text-right font-bold text-[#C5A059]">{brl(parceiros.reduce((s, p) => s + p.comissao_periodo, 0))}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Séries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#111827] rounded-2xl border border-white/5 p-6" data-testid="chart-companies">
          <div className="mb-6">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#C5A059]" />Evolução de Clientes
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Novas empresas por mês · últimos 12 meses</p>
          </div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series.novas_empresas} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs><linearGradient id="gradCompanies" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={GOLD} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                </linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#1E293B' }} tickLine={false} />
                <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="new_companies" name="Novas Empresas" stroke={GOLD} strokeWidth={2.5}
                  fill="url(#gradCompanies)" dot={{ fill: GOLD, r: 4, strokeWidth: 0 }} activeDot={{ r: 6, fill: GOLD }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#111827] rounded-2xl border border-white/5 p-6" data-testid="chart-feedbacks">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <BarChart3 className="w-4 h-4" style={{ color: GREEN }} />Engajamento Mensal
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">Avaliações e promotores por mês</p>
            </div>
            <div className="text-right" data-testid="reviews-variation">
              <p className="text-lg font-bold text-white">{avaliacoes.total_periodo}</p>
              <p className="text-[10px] text-slate-500">avaliações · {period.label}</p>
              <p className={`text-[11px] font-bold flex items-center gap-0.5 justify-end ${avaliacoes.variacao_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {avaliacoes.variacao_pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {avaliacoes.variacao_pct > 0 ? '+' : ''}{avaliacoes.variacao_pct}% vs período anterior ({avaliacoes.periodo_anterior})
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">{avaliacoes.total_historico} no histórico</p>
            </div>
          </div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <ReBarChart data={series.avaliacoes} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#1E293B' }} tickLine={false} />
                <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="total" name="Total Avaliações" fill={NAVY} stroke="#1E3A5F" radius={[4, 4, 0, 0]} />
                <Bar dataKey="promoters" name="Promotores" fill={GREEN} radius={[4, 4, 0, 0]} />
              </ReBarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-[#111827] rounded-2xl border border-white/5 p-6" data-testid="chart-revenue">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <DollarSign className="w-4 h-4" style={{ color: GREEN }} />Faturamento mês a mês · previsto x realizado
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Previsto = boletos com vencimento no mês · Realizado = pagamento confirmado · só assinaturas do Falo Com o Dono
              {faturamento.fonte === 'asaas' ? ' (Asaas)' : ' (histórico interno)'}
            </p>
            {faturamento.ignorado_outros_produtos > 0 && (
              <p className="text-[11px] text-slate-500 mt-1" data-testid="revenue-ignored">
                {brl(faturamento.ignorado_outros_produtos)} recebidos no período são de outros produtos seus no Asaas e ficaram fora desta conta
              </p>
            )}
          </div>
          <div className="flex gap-6 text-right">
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Previsto 12m</p>
              <p className="text-lg font-bold" style={{ color: GOLD }} data-testid="revenue-forecast-12m">
                {brl(series.faturamento.reduce((s, m) => s + (m.previsto || 0), 0))}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Realizado 12m</p>
              <p className="text-lg font-bold" style={{ color: GREEN }} data-testid="revenue-total-12m">
                {brl(series.faturamento.reduce((s, m) => s + (m.total || 0), 0))}
              </p>
            </div>
          </div>
        </div>
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <ReBarChart data={series.faturamento} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#1E293B' }} tickLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? `R$${Math.round(v / 1000)}k` : `R$${v}`)} />
              <Tooltip content={<CustomTooltip money />} />
              <Bar dataKey="previsto" name="Previsto" fill={GOLD} radius={[4, 4, 0, 0]} />
              <Bar dataKey="total" name="Realizado" fill={GREEN} radius={[4, 4, 0, 0]} />
            </ReBarChart>
          </ResponsiveContainer>
        </div>

        <div className="overflow-x-auto mt-5">
          <table className="w-full text-xs" data-testid="revenue-month-table">
            <thead>
              <tr className="text-slate-500 border-b border-white/10">
                <th className="text-left py-2 pr-3 font-medium">Mês</th>
                <th className="text-right py-2 pr-3 font-medium">Previsto</th>
                <th className="text-right py-2 pr-3 font-medium">Realizado</th>
                <th className="text-right py-2 pr-3 font-medium">% do previsto</th>
                <th className="text-right py-2 font-medium">Cobranças pagas</th>
              </tr>
            </thead>
            <tbody>
              {[...series.faturamento].reverse().map(m => {
                const pct = m.previsto ? (m.total / m.previsto) * 100 : null;
                return (
                  <tr key={m.month} className="border-b border-white/5" data-testid={`revenue-row-${m.month}`}>
                    <td className="py-2 pr-3 text-slate-200">{m.month}</td>
                    <td className="py-2 pr-3 text-right" style={{ color: GOLD }}>{brl(m.previsto || 0)}</td>
                    <td className="py-2 pr-3 text-right font-semibold" style={{ color: GREEN }}>{brl(m.total || 0)}</td>
                    <td className="py-2 pr-3 text-right text-slate-300">{pct == null ? '—' : `${pct.toFixed(0)}%`}</td>
                    <td className="py-2 text-right text-slate-400">{m.count || 0}/{m.previsto_count || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardTab;
